import { Controller } from "@hotwired/stimulus"
import { savePlan } from "plan_storage"
import { renderIcons } from "lucide_icons"

// Goal-references editor — manages a list of reference documents (a
// citation/title + optional link) attached to a single goal. Shown just
// below the goal in the editor and carried into the printed PDF so a
// facilitator can cite the DDS directives / statutes that justify the
// goal. One controller instance per goal row; the owning goal is
// identified by the parent [data-row]'s data-id (mirrors goal-resources).
//
// State lives at plan.goals[goalIdx].references = [{ id, title, url }].
const STORAGE_KEY = "pcp_plan_data"

const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
}[c]))

// Quick-add presets. The workspace manages its own list in Settings →
// Reference documents; the Rails-native editor injects it as
// window.PCPM_REFERENCE_DOCS. These built-in California DDS / Lanterman Act
// citations are the FALLBACK when nothing is injected (e.g. the standalone
// iframe editor) or the list is empty. Keep in sync with
// ReferenceDocument::DEFAULTS on the server.
const DEFAULT_PRESETS = [
  { chip: "DDS §4688.22", title: "DDS Social Recreation, Camping & Nonmedical Therapies Directive (W&I Code §4688.22)", url: "https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=WIC&sectionNum=4688.22" },
  { chip: "IPP §4646",    title: "Individual Program Plan — IPP (W&I Code §4646)", url: "https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=WIC&sectionNum=4646" },
  { chip: "POS §4648",    title: "Purchase of Services (W&I Code §4648)", url: "https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=WIC&sectionNum=4648" },
  { chip: "SDP §4685.8",  title: "Self-Determination Program (W&I Code §4685.8)", url: "https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=WIC&sectionNum=4685.8" },
  { chip: "Lanterman Act", title: "Lanterman Developmental Disabilities Services Act (W&I Code §4500 et seq.)", url: "https://leginfo.legislature.ca.gov/faces/codes_displayexpandedbranch.xhtml?lawCode=WIC&division=4.5." }
]

// The active preset list: the workspace's own (injected) or the fallback.
function presets() {
  const injected = (typeof window !== "undefined" && Array.isArray(window.PCPM_REFERENCE_DOCS))
    ? window.PCPM_REFERENCE_DOCS.filter(p => p && p.chip && p.title)
    : null
  return (injected && injected.length) ? injected : DEFAULT_PRESETS
}

export default class extends Controller {
  static values = { goalId: String }
  static targets = ["list"]

  connect() {
    this.plan = this._load()
    // dynamic-list assigns the row's data-id AFTER cloning the template,
    // so watch for it (mirrors goal-resources).
    const row = this.element.closest("[data-row]")
    if (row) {
      this._rowObserver = new MutationObserver(() => {
        if (row.dataset.id && row.dataset.id !== this._knownGoalId) {
          this._knownGoalId = row.dataset.id
          this.render()
        }
      })
      this._rowObserver.observe(row, { attributes: true, attributeFilter: ["data-id"] })
      if (row.dataset.id) this._knownGoalId = row.dataset.id
    }
    this.render()
    this._onChange = (e) => {
      if (this._suppress) return
      this.plan = (e.detail && e.detail.plan) || this._load()
      this.render()
    }
    document.addEventListener("plan:change", this._onChange)
  }

  disconnect() {
    document.removeEventListener("plan:change", this._onChange)
    if (this._rowObserver) this._rowObserver.disconnect()
  }

  addReference() {
    const g = this._goal()
    if (!g) return
    if (!Array.isArray(g.references)) g.references = []
    const id = Date.now() + Math.random()
    g.references.push({ id, title: "", url: "" })
    this._persist()
    this.render()
    requestAnimationFrame(() => {
      const el = this.hasListTarget && this.listTarget.querySelector(`[data-ref-id="${id}"][data-field="title"]`)
      if (el) el.focus({ preventScroll: true })
    })
  }

  quickAdd(event) {
    const g = this._goal()
    if (!g) return
    if (!Array.isArray(g.references)) g.references = []
    const p = presets()[parseInt(event.currentTarget.dataset.preset, 10)]
    if (!p) return
    // Don't add the same directive twice.
    if (g.references.some(r => (r.title || "").trim() === p.title)) return
    g.references.push({ id: Date.now() + Math.random(), title: p.title, url: p.url })
    this._persist()
    this.render()
  }

  removeReference(event) {
    const g = this._goal()
    if (!g || !Array.isArray(g.references)) return
    const id = event.currentTarget.dataset.refId
    g.references = g.references.filter(r => String(r.id) !== String(id))
    this._persist()
    this.render()
  }

  // Text edits update the model in place and persist WITHOUT re-rendering
  // (our own plan:change is suppressed) so the input keeps focus.
  updateReferenceField(event) {
    const el = event.currentTarget
    const g = this._goal()
    if (!g || !Array.isArray(g.references)) return
    const ref = g.references.find(r => String(r.id) === String(el.dataset.refId))
    if (!ref) return
    ref[el.dataset.field] = el.value
    this._persist()
  }

  // Save a goal reference into the workspace's reference library (the DB rows
  // managed in Settings), so it becomes a Quick-add chip everywhere. POSTs to
  // the reference_documents create action; on success we push it into the
  // in-page preset list so the row's check-mark appears immediately.
  async addToDatabase(event) {
    event.preventDefault()
    const g = this._goal()
    if (!g || !Array.isArray(g.references)) return
    const ref = g.references.find(r => String(r.id) === String(event.currentTarget.dataset.refId))
    if (!ref) return
    const title = String(ref.title || "").trim()
    if (!title) { this._toast("Add a name first."); return }
    if (this._refInDb(ref)) { this.render(); return }

    const chip  = this._deriveChip(title)
    const token = document.querySelector('meta[name="csrf-token"]')?.content || ""
    const btn   = event.currentTarget
    btn.disabled = true
    try {
      const res = await fetch("/reference_documents", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ reference_document: { chip, title, url: String(ref.url || "").trim() } })
      })
      const json = await res.json().catch(() => null)
      if (res.ok && json && json.ok && json.document) {
        if (Array.isArray(window.PCPM_REFERENCE_DOCS)) window.PCPM_REFERENCE_DOCS.push(json.document)
        else window.PCPM_REFERENCE_DOCS = [json.document]
        this.render()   // repaint → check-mark replaces the save button
        this._toast(`Saved “${chip}” to your reference library.`)
      } else {
        btn.disabled = false
        this._toast((json && json.errors && json.errors.join(", ")) || "Couldn't save to library.")
      }
    } catch (_) {
      btn.disabled = false
      this._toast("Couldn't save — check your connection.")
    }
  }

  // ---- reference-library helpers ----
  _normTitle(s) { return String(s == null ? "" : s).trim().toLowerCase().replace(/\s+/g, " ") }
  _normUrl(s)   { return String(s == null ? "" : s).trim().toLowerCase().replace(/\/+$/, "") }

  // True when a goal reference already exists in the workspace library (the
  // Quick-add presets injected from Settings) — matched by title or link.
  _refInDb(r) {
    const t = this._normTitle(r.title), u = this._normUrl(r.url)
    if (!t && !u) return false
    return presets().some(p =>
      (t && this._normTitle(p.title) === t) ||
      (u && this._normUrl(p.url) === u)
    )
  }

  // Short pill label for a saved reference: a "§section" citation if present,
  // else the label before the first — : or -, capped at 30 chars.
  _deriveChip(title) {
    const t = String(title || "").trim().replace(/\s+/g, " ")
    const sec = t.match(/§\s*[\d.]+/)
    let base = (t.split(/\s*[—:–-]\s*/)[0] || t).trim()
    if (sec && !base.includes("§")) base = `${base} ${sec[0].replace(/\s+/g, "")}`.trim()
    if (base.length > 30) base = base.slice(0, 30).trim()
    return base || "Reference"
  }

  _toast(msg) {
    let bar = document.getElementById("en-toast")
    if (!bar) {
      bar = document.createElement("div")
      bar.id = "en-toast"
      bar.style.cssText = "position:fixed;top:12px;right:12px;padding:10px 14px;border-radius:8px;font:600 13px/1.2 ui-sans-serif,system-ui;z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,.18);background:#eef2ff;color:#3730a3;pointer-events:none;max-width:60vw;"
      document.body.appendChild(bar)
    }
    bar.textContent = msg
    bar.style.opacity = "1"
    clearTimeout(this._toastTimer)
    this._toastTimer = setTimeout(() => { bar.style.opacity = "0" }, 3200)
  }

  render() {
    if (!this.hasListTarget) return
    const g = this._goal()
    const refs = (g && Array.isArray(g.references)) ? g.references : []
    const rows = refs.map(r => `
      <div class="flex items-center gap-1 mb-1.5">
        <i data-lucide="file-text" class="w-3.5 h-3.5 text-gray-400 flex-shrink-0"></i>
        <span class="text-[9px] font-bold uppercase text-gray-400 flex-shrink-0">Name</span>
        <input type="text" placeholder="e.g. DDS Social Rec Directive §4688.22" value="${esc(r.title || "")}"
               data-action="input->goal-references#updateReferenceField"
               data-ref-id="${esc(r.id)}" data-field="title"
               class="flex-1 min-w-0 p-1 border border-gray-300 rounded text-xs">
        <span class="text-[9px] font-bold uppercase text-gray-400 flex-shrink-0">Link</span>
        <input type="url" placeholder="https://… (optional)" value="${esc(r.url || "")}"
               data-action="input->goal-references#updateReferenceField"
               data-ref-id="${esc(r.id)}" data-field="url"
               class="w-40 flex-shrink-0 p-1 border border-gray-300 rounded text-xs">
        ${this._refInDb(r)
          ? `<span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 text-[10px] font-semibold flex-shrink-0 leading-none" title="Already in your reference library">
               <i data-lucide="check" class="w-3 h-3"></i> In library
             </span>`
          : ((r.title || "").trim()
              ? `<button type="button" data-action="goal-references#addToDatabase" data-ref-id="${esc(r.id)}"
                        class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 text-[10px] font-semibold flex-shrink-0 leading-none"
                        title="Save to your reference library (adds a Quick-add chip)"
                        aria-label="Save to reference library"><i data-lucide="database" class="w-3 h-3"></i> Save</button>`
              : `<span class="flex-shrink-0"></span>`)}
        <button type="button" data-action="goal-references#removeReference" data-ref-id="${esc(r.id)}"
                class="p-1 dl-row-remove leading-none flex-shrink-0" title="Remove reference"
                aria-label="Remove reference">&times;</button>
      </div>`).join("")
    // The quick-add chips moved to the right-side panel (reference-panel
    // controller). Here we just show the trigger; clicking it (or anywhere in
    // this section) slides the library panel in from the right.
    this.listTarget.innerHTML = `
      ${rows || `<p class="text-[10px] italic text-gray-400 py-1">No reference documents yet.</p>`}
      <div class="mt-1">
        <button type="button" data-reference-quickadd
                class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100">
          <i data-lucide="book-marked" class="w-3 h-3"></i> Quick add from library
        </button>
      </div>`
    renderIcons(this.element)
  }

  _goal() {
    const id = this._knownGoalId || this.goalIdValue
    if (!id) return null
    if (!Array.isArray(this.plan?.goals)) return null
    return this.plan.goals.find(g => String(g.id) === String(id))
  }
  _persist() {
    savePlan(this.plan)
    this._suppress = true
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: "goals", plan: this.plan, source: "goal-references", nested: true }
    }))
    this._suppress = false
  }
  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }
}
